/**
 * The Config Studio's own API client.
 *
 * It does not go through `lib/api.ts`. That interface is implemented twice — once by the mock
 * adapter that ships the demo and once by `httpApi` — so every method added to it has to be added
 * to both, and three other agents are writing in this repository today. The Studio is a self
 * contained screen against `/api/v1/studio/*`, so its client lives beside it and reuses the one
 * thing it must not duplicate: the session's CSRF token, which `httpApi` owns and refreshes.
 *
 * Author: Danish Khan
 */
import { currentCsrf } from '../lib/httpApi.ts';
import { ApiError } from '../lib/apiTypes.ts';

const BASE = '/api/v1/studio';

export type KnobKind = 'number' | 'string' | 'boolean' | 'enum' | 'list' | 'object' | 'localized' | 'markdown';

export interface KnobReader {
  file: string;
  symbol: string;
  line: number;
  plane: 'runtime' | 'console' | 'lab';
}

export interface KnobValue {
  file: string;
  pointer: string;
  /** The `*` segments — `['budget']` for `slots.budget.ask_hint`. Labels the control. */
  instance: string[];
  value: unknown;
}

export interface Knob {
  field: string;
  area: string;
  label: string;
  help: string;
  file: string;
  pointer: string;
  kind: KnobKind;
  localized: boolean;
  options?: string[];
  /** False when nothing in the runtime reads this field: the control is disabled. */
  live: boolean;
  note: string;
  reader: (KnobReader & { evidence: string }) | null;
  readers: KnobReader[];
  values: KnobValue[];
}

export interface Version {
  id: string;
  digest: string;
  kind: 'disk' | 'studio';
  baseDigest: string | null;
  notes: string | null;
  proposalId: string | null;
  isEffective: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  effectiveSince: string | null;
  effectiveBy: string | null;
  loadedAt: string;
  summary: Record<string, unknown>;
  files: string[];
}

/**
 * Whether a version is actually in force in the running process.
 *
 * The database and the process are two different places, and until the API started saying this out
 * loud only the database was ever shown. The live incident: a daily send ceiling was raised from 12
 * to 24 at 15:20, this screen showed the new version as effective, and the agent went on using 12
 * until an unrelated deploy restarted the process at 15:35. Rollback is the worse direction — an
 * operator watching the agent say something wrong presses Roll back, sees the pill move, and stops
 * looking while the agent carries on saying it.
 *
 * Two endpoints answer in this shape, and the pair is the point. A decision (`approve`, `rollback`)
 * reports what its own swap did; `config()` reports what the registry holds RIGHT NOW, so the
 * screen keeps telling the truth after a reload instead of falling back to the row.
 *
 * `inForce: false` is not an error. The approval is committed, recorded and audited; the honest
 * sentence is "it is approved and this process is still answering from the previous one — restart
 * h1-core", and `detail` is the API's own wording of it.
 */
export interface InForce {
  /** True when the running process is answering from this version. */
  inForce: boolean;
  /**
   * The digest in force in the process: what a swap loaded, or what a read found loaded — which on
   * a `false` names the configuration customers are actually getting. Null when neither can say.
   */
  digest: string | null;
  /** One sentence for the screen. Says what to do when it is not in force. */
  detail: string;
}

export interface ValidationIssue {
  field: string | null;
  file: string | null;
  message: string;
}

export interface Validation {
  ok: boolean;
  digest: string | null;
  issues: ValidationIssue[];
  checked: number;
  unreachable: string[];
  todoHi: string[];
}

export interface Edit {
  field: string;
  file: string;
  pointer: string;
  before?: unknown;
  after?: unknown;
}

export interface Proposal {
  id: string;
  status: 'open' | 'approved' | 'rejected' | 'withdrawn';
  author: string;
  reason: string;
  baseDigest: string;
  digest: string | null;
  valid: boolean;
  validation: Validation;
  edits: Edit[];
  createdAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionReason: string | null;
  versionId: string | null;
}

export interface StudioConfig {
  tenant: { id: string; status: string; languages: string[]; timezone: string };
  version: Version;
  drift: { baseDigest: string | null; diskDigest: string; drifted: boolean; shadowed: string[] };
  areas: { area: string; knobs: Knob[] }[];
  openProposals: number;
  may: { propose: boolean; approve: boolean };
  /**
   * Whether `version` is the one the process on the other end is actually answering from.
   *
   * The same shape a decision's `effective` carries, and the reason the pill can be honest on a
   * RELOAD: the API compares the running pack's digest against the effective row on every read,
   * so the truth no longer lasts only for the page-load that pressed the button.
   */
  inForce: InForce;
}

export interface TierCounts { hot: number; warm: number; cold: number }

export interface Simulation {
  leads: number;
  window: string;
  versionBefore: string;
  versionAfter: string;
  before: TierCounts;
  after: TierCounts;
  moved: number;
  movements: { from: string; to: string; count: number }[];
  staleStored: number;
  samples: {
    leadId: string;
    before: { score: number; tier: string };
    after: { score: number; tier: string };
    stored: { score: number; tier: string; version: string | null };
  }[];
  unsupported: { before: string[]; after: string[] };
}

export interface StudioClient {
  config(): Promise<StudioConfig>;
  versions(): Promise<Version[]>;
  proposals(): Promise<Proposal[]>;
  propose(input: { reason: string; edits: Edit[] }): Promise<Proposal>;
  approve(id: string): Promise<{ proposal: Proposal; version: Version; effective: InForce }>;
  reject(id: string, reason: string): Promise<Proposal>;
  simulate(id: string): Promise<{ simulation: Simulation | null; detail: string }>;
  /**
   * Returns the version rolled back TO and whether this process actually went back to it. The
   * `effective` half is the point: a rollback that only moved the pill is the failure it exists
   * to end, so the caller is given the outcome rather than being left to assume the happy one.
   */
  rollback(digest: string, reason: string): Promise<{ version: Version; effective: InForce }>;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const headers = new Headers(init?.headers);
  if (method !== 'GET') {
    // Only when there is a body: `createApp` parses `application/json` with a raw parser and a
    // bodyless POST that claims the type answers 400 as plain text. `httpApi` learned this in a
    // browser, and the Studio inherits the lesson rather than repeating it.
    if (init?.body !== undefined) headers.set('content-type', 'application/json');
    const csrf = currentCsrf();
    if (csrf) headers.set('x-csrf', csrf);
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'same-origin' });
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* handled below */ }
  if (!res.ok) {
    const detail = (body ?? {}) as { error?: { code?: string; message?: string; issues?: { path: string; message: string }[] } };
    throw new ApiError(detail.error?.message ?? `${method} ${path} failed`, res.status, {
      ...(detail.error?.code ? { code: detail.error.code } : {}),
      ...(detail.error?.issues ? { issues: detail.error.issues } : {}),
    });
  }
  return body as T;
}

const json = (value: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(value) });

export const studioApi: StudioClient = {
  config: () => call<StudioConfig>('/config'),
  versions: async () => (await call<{ versions: Version[] }>('/versions')).versions,
  proposals: async () => (await call<{ proposals: Proposal[] }>('/proposals')).proposals,
  propose: async (input) => (await call<{ proposal: Proposal }>('/proposals', json(input))).proposal,
  approve: (id) => call<{ proposal: Proposal; version: Version; effective: InForce }>(`/proposals/${id}/approve`, json({})),
  reject: async (id, reason) => (await call<{ proposal: Proposal }>(`/proposals/${id}/reject`, json({ reason }))).proposal,
  simulate: (id) => call<{ simulation: Simulation | null; detail: string }>(`/proposals/${id}/simulate`, json({})),
  rollback: (digest, reason) => call<{ version: Version; effective: InForce }>('/rollback', json({ digest, reason })),
};

/* ------------------------------------------------------------------ value helpers */

/** The key an edit is addressed by. One value per (file, pointer), so a draft cannot fork. */
export const editKey = (file: string, pointer: string): string => `${file}|${pointer}`;

/**
 * Is this per-language value complete for the languages the tenant declares?
 *
 * The console refuses a partial save before the API does, because the API's refusal arrives after
 * the manager has typed the whole thing and pressed a button. Same rule, said earlier.
 */
export function missingLanguages(value: unknown, languages: string[]): string[] {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return languages;
  const map = value as Record<string, unknown>;
  return languages.filter((l) => typeof map[l] !== 'string' || (map[l] as string).trim() === '');
}

/** What a value looks like in a one-line diff. Objects as compact JSON, strings as themselves. */
export function showValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
